import { BasePointDragger } from "./BasePointDragger";
import { DraggerType, type Point3D, type TypedInputMode } from "./types";
import {
  IDENTITY_TRANSFORM,
  rotationTransform,
  type EntityTransform,
} from "../transform/entityTransform";

/**
 * Interactive "rotate around base point" tool — AutoCAD ROTATE. Pick a base
 * point (the pivot), then point in the direction the entities should face.
 *
 * The angle is measured absolutely from the positive X axis, matching AutoCAD's
 * default: pointing due east of the pivot means zero rotation. Polar snap gives
 * the usual clean increments, which is what makes this practical on a tablet.
 * The same angle can be typed on the keypad instead of pointed at, where it
 * reads as the rotation to apply (0 = leave alone, 90 = quarter turn).
 */
export class RotateDragger extends BasePointDragger {
  readonly type = DraggerType.Rotate;

  protected readonly followUpPointCount = 1;

  /**
   * A single mode, so the pad shows no mode selector: a rotation has an angle
   * and nothing else. X/Y would have to be read as a direction vector, and a
   * length has no meaning at all here (the radius below is pure decoration).
   */
  private static readonly ANGLE_INPUT_MODES: TypedInputMode[] = [
    { id: "angle", fields: [{ key: "angleDeg", icon: "angle" }] },
  ];

  /** Guide-line length used when an angle is typed before any preview. */
  private static readonly DEFAULT_RUBBER_BAND_PX = 100;

  protected computeTransform(current: Point3D): EntityTransform {
    const base = this.basePoint;
    if (!base) return { ...IDENTITY_TRANSFORM };

    const dx = current.x - base.x;
    const dy = current.y - base.y;
    // Exactly on the pivot: no direction to read, so no rotation.
    if (dx === 0 && dy === 0) return { ...IDENTITY_TRANSFORM };

    return rotationTransform(Math.atan2(dy, dx), base);
  }

  protected commit(transform: EntityTransform): void {
    this.commitTransformInPlace(transform);
  }

  protected promptKey(): string {
    return this.basePoint
      ? "canvas:selection.basePointPrompt.angle"
      : "canvas:selection.basePointPrompt.base";
  }

  // ============== Typed input: the rotation angle ==============
  //
  // The typed value is the rotation itself, counter-clockwise positive: 0
  // leaves the selection alone, 90 is a quarter turn. That needs no conversion
  // because computeTransform() above already rotates BY the pointer's bearing
  // rather than TO it — pointing due east is zero rotation. Typing 200 lands on
  // the same matrix as -160 once atan2 normalizes it, which is the same turn.

  protected override typedInputModes(): TypedInputMode[] | null {
    return this.basePoint ? RotateDragger.ANGLE_INPUT_MODES : null;
  }

  protected override typedInputTitleKey(): string {
    return "canvas:tools.options.keypad.title.rotate";
  }

  protected override typedInputValues(
    current: Point3D | null
  ): Record<string, number> {
    const base = this.basePoint;
    if (!base || !current) return { angleDeg: 0 };
    const dx = current.x - base.x;
    const dy = current.y - base.y;
    if (dx === 0 && dy === 0) return { angleDeg: 0 };
    return { angleDeg: (Math.atan2(dy, dx) * 180) / Math.PI };
  }

  protected override worldPointFromTypedValues(
    _modeId: string,
    values: Record<string, number>
  ): Point3D | null {
    const base = this.basePoint;
    const angleDeg = values.angleDeg;
    if (!base || !Number.isFinite(angleDeg)) return null;

    const theta = (angleDeg * Math.PI) / 180;
    const radius = this.rubberBandRadius(base);
    return {
      x: base.x + radius * Math.cos(theta),
      y: base.y + radius * Math.sin(theta),
      z: base.z,
    };
  }

  /**
   * How far from the base to put the synthesized point. Only the direction
   * matters to computeTransform(), but the guide line runs to this point, so
   * reuse the radius the pointer last had, falling back to a fixed on-screen
   * length when nothing has been previewed yet — right at any zoom.
   */
  private rubberBandRadius(base: Point3D): number {
    const current = this.lastPreviewWorld;
    if (current) {
      const radius = Math.hypot(current.x - base.x, current.y - base.y);
      if (radius > 0) return radius;
    }
    const fallback = this.viewer.screenDistanceToWorld(
      RotateDragger.DEFAULT_RUBBER_BAND_PX
    );
    return fallback || 1;
  }
}
